/**
 * Display timestamps in Australia/Sydney (AEST/AEDT).
 * Use for UI instants stored/sent as UTC ISO. Do not use for calendar-only
 * trading days / gaming_date strings that are already date-only.
 */

export const SYDNEY_TZ = 'Australia/Sydney'

/**
 * @param {string|number|Date|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} [opts]
 * @returns {string}
 */
export function formatSydneyDateTime(value, opts = {}) {
  if (value == null || value === '') return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) {
    // Legacy non-ISO display string — show as-is
    if (typeof value === 'string' && value.trim()) return value.trim()
    return '—'
  }
  const {
    dateStyle = 'short',
    timeStyle = 'short',
    ...rest
  } = opts
  try {
    return d.toLocaleString('en-AU', {
      timeZone: SYDNEY_TZ,
      dateStyle,
      timeStyle,
      ...rest,
    })
  } catch {
    return d.toISOString()
  }
}

/**
 * Date only in Sydney calendar (for instants that should show the Sydney day).
 * @param {string|number|Date|null|undefined} value
 */
export function formatSydneyDate(value, opts = {}) {
  if (value == null || value === '') return '—'
  // Already a plain calendar date — do not shift via UTC midnight
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [y, m, day] = value.trim().split('-').map(Number)
    const d = new Date(Date.UTC(y, m - 1, day, 12, 0, 0))
    try {
      return d.toLocaleDateString('en-AU', {
        timeZone: SYDNEY_TZ,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        ...opts,
      })
    } catch {
      return value.trim()
    }
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : '—'
  try {
    return d.toLocaleDateString('en-AU', {
      timeZone: SYDNEY_TZ,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      ...opts,
    })
  } catch {
    return '—'
  }
}

/**
 * Time-only portion in Sydney (e.g. end of a range after a full datetime).
 */
export function formatSydneyTime(value, opts = {}) {
  if (value == null || value === '') return '—'
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  try {
    return d.toLocaleTimeString('en-AU', {
      timeZone: SYDNEY_TZ,
      hour: '2-digit',
      minute: '2-digit',
      ...opts,
    })
  } catch {
    return '—'
  }
}
